import { type Category, type Population } from '@prisma/client';
import { linko } from '@shared/linko';
import { Button, Stack, TextField, IconButton, InputAdornment, Card, CardContent, Typography, FormControl, RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import React, { useEffect, useMemo, useState } from 'react';
import { useAlert } from '../../contexts/AlertProvider';
import { GUEST_FEED_PATH, USER_REQUEST_PATH } from '@shared/helpers/constants';

interface CreateLinksProps {

}

const FAKE_CATEGORIES: Omit<Category, 'organizationId' | 'createdAt' | 'updatedAt'>[] = [
    {
        id: 'fake-cat-1',
        name: 'עזרה בבית',
        isSensitive: false,
        isHidden: false,
        isHiddenFromPreferences: false,
        type: 'AID_REQUEST',
        aidRequestFormId: '',
        imagePath: '',
        sortOrder: 1,
    },
    {
        id: 'fake-cat-2',
        name: 'סיוע לקשישים',
        isSensitive: false,
        isHidden: false,
        isHiddenFromPreferences: false,
        type: 'AID_REQUEST',
        aidRequestFormId: '',
        imagePath: '',
        sortOrder: 2,
    },
    {
        id: 'fake-cat-3',
        name: 'תמיכה רגשית',
        isSensitive: false,
        isHidden: false,
        isHiddenFromPreferences: false,
        type: 'AID_REQUEST',
        aidRequestFormId: '',
        imagePath: '',
        sortOrder: 3,
    },
];

const FAKE_POPULATIONS: Omit<Population, 'organizationId' | 'createdAt' | 'updatedAt'>[] = [
    {
        id: 'fake-pop-1',
        name: 'חיילים',
        icon: '',
    },
    {
        id: 'fake-pop-2',
        name: 'קשישים',
        icon: '',
    },
    {
        id: 'fake-pop-3',
        name: 'סטודנטים',
        icon: '',
    },
];

type LinkType = 'request' | 'volunteer';
type SelectionMode = 'single' | 'multiple';

type LinkSubCategory = {
    id: string;
    name: string;
    isLinkOnly?: boolean;
};

type LinkCategory = Category & {
    subCategories?: LinkSubCategory[];
};

const CreateLinks: React.FC<CreateLinksProps> = (props) => {
    const { setAlertText, setSeverity } = useAlert();
    const [categories, setCategories] = useState<LinkCategory[]>([]);
    const [populations, setPopulations] = useState<Population[]>([]);
    const [organizationId, setOrganizationId] = useState<string>('');
    const [linkType, setLinkType] = useState<LinkType>('volunteer');
    const [selectionMode, setSelectionMode] = useState<SelectionMode>('single');
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
    const [selectedSubCategoryId, setSelectedSubCategoryId] = useState('');
    const [selectedPopulationIds, setSelectedPopulationIds] = useState<string[]>([]);
    const [generatedLink, setGeneratedLink] = useState<string>('');

    useEffect(() => {
        const fetchWemeOrg = async () => {
            const { data: wemeOrg } = await linko.organization.queries.getMarketPlaceOrganization();
            const fetchedCategories = wemeOrg?.categories || [];
            const fetchedPopulations = wemeOrg?.populations || [];
            setOrganizationId(wemeOrg?.id || '');

            if (fetchedCategories.length === 0) {
                const fakeCategories = FAKE_CATEGORIES.map(cat => ({
                    ...cat,
                    organizationId: '',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                })) as Category[];
                setCategories(fakeCategories);
            } else {
                setCategories(fetchedCategories as LinkCategory[]);
            }

            if (fetchedPopulations.length === 0) {
                const fakePopulations = FAKE_POPULATIONS.map(pop => ({
                    ...pop,
                    organizationId: '',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                })) as Population[];
                setPopulations(fakePopulations);
            } else {
                setPopulations(fetchedPopulations);
            }
        };
        fetchWemeOrg();
    }, []);

    const handleSelectionModeChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setSelectionMode(value as SelectionMode);
        setSelectedCategoryIds([]);
        setSelectedSubCategoryId('');
        setSelectedPopulationIds([]);
        setGeneratedLink('');
    };

    const linkOnlySubCategories = useMemo(
        () =>
            categories.flatMap((category) =>
                (category.subCategories ?? [])
                    .filter((sub) => sub.isLinkOnly)
                    .map((sub) => ({
                        ...sub,
                        parentId: category.id,
                        parentName: category.name,
                    })),
            ),
        [categories],
    );

    const handleCategoryChangeSingle = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setSelectedCategoryIds(value ? [value] : []);
        if (value) setSelectedSubCategoryId('');
        setGeneratedLink('');
    };

    const handleCategoryChangeMultiple = (categoryId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedCategoryIds(prev => [...prev, categoryId]);
            setSelectedSubCategoryId('');
        } else {
            setSelectedCategoryIds(prev => prev.filter(id => id !== categoryId));
        }
        setGeneratedLink('');
    };

    const handleSubCategoryChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setSelectedSubCategoryId(value);
        if (value) setSelectedCategoryIds([]);
        setGeneratedLink('');
    };

    const handlePopulationChangeSingle = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setSelectedPopulationIds(value ? [value] : []);
        setGeneratedLink('');
    };

    const handlePopulationChangeMultiple = (populationId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedPopulationIds(prev => [...prev, populationId]);
        } else {
            setSelectedPopulationIds(prev => prev.filter(id => id !== populationId));
        }
        setGeneratedLink('');
    };

    const handleLinkTypeChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setLinkType(value as LinkType);
        setGeneratedLink('');
    };

    const handleGenerateLink = () => {
        const selectedSub = linkOnlySubCategories.find(
            (sub) => sub.id === selectedSubCategoryId,
        );
        if (!selectedSub && selectedCategoryIds.length === 0) {
            return;
        }

        const baseDomain = import.meta.env.MODE === 'production'
            ? 'https://www.we-me.app'
            : 'http://localhost:5173';

        const searchParams = new URLSearchParams();
        if (selectedSub) {
            searchParams.append('subCategory', selectedSub.id);
            if (linkType === 'request') {
                searchParams.append('category', selectedSub.parentId);
            }
        } else {
            selectedCategoryIds.forEach(categoryId => {
                searchParams.append('category', categoryId);
            });
        }
        selectedPopulationIds.forEach(populationId => {
            searchParams.append('population', populationId);
        });

        let link: string;
        if (linkType === 'request') {
            link = `${baseDomain}${USER_REQUEST_PATH}?${searchParams.toString()}`;
        } else {
            link = `${baseDomain}${GUEST_FEED_PATH}?${searchParams.toString()}`;
        }

        setGeneratedLink(link);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(generatedLink);
        setAlertText('הקישור הועתק ללוח');
        setSeverity('success');
    };

    return (
        <Stack spacing={3} sx={{ padding: 3, maxWidth: '100%', overflow: 'visible' }}>
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        סוג קישור
                    </Typography>
                    <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                            value={linkType}
                            onChange={handleLinkTypeChange}
                            row
                        >
                            <FormControlLabel
                                value="volunteer"
                                control={
                                    <Radio
                                        sx={{
                                            '&.Mui-checked': {
                                                color: 'primary.main',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body1">
                                        מתנדב
                                    </Typography>
                                }
                                sx={{
                                    marginLeft: 0,
                                    marginRight: 2,
                                }}
                            />
                            <FormControlLabel
                                value="request"
                                control={
                                    <Radio
                                        sx={{
                                            '&.Mui-checked': {
                                                color: 'primary.main',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body1">
                                        בקשה
                                    </Typography>
                                }
                                sx={{
                                    marginLeft: 0,
                                    marginRight: 0,
                                }}
                            />
                        </RadioGroup>
                    </FormControl>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        מצב בחירה
                    </Typography>
                    <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                            value={selectionMode}
                            onChange={handleSelectionModeChange}
                            row
                        >
                            <FormControlLabel
                                value="single"
                                control={
                                    <Radio
                                        sx={{
                                            '&.Mui-checked': {
                                                color: 'primary.main',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body1">
                                        בחירה אחת
                                    </Typography>
                                }
                                sx={{
                                    marginLeft: 0,
                                    marginRight: 2,
                                }}
                            />
                            <FormControlLabel
                                value="multiple"
                                control={
                                    <Radio
                                        sx={{
                                            '&.Mui-checked': {
                                                color: 'primary.main',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body1">
                                        בחירות מרובות
                                    </Typography>
                                }
                                sx={{
                                    marginLeft: 0,
                                    marginRight: 0,
                                }}
                            />
                        </RadioGroup>
                    </FormControl>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        קטגוריה
                    </Typography>
                    <FormControl component="fieldset" fullWidth>
                        {selectionMode === 'single' ? (
                            <RadioGroup
                                value={selectedCategoryIds[0] || ''}
                                onChange={handleCategoryChangeSingle}
                                row
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {categories.map((category) => (
                                    <FormControlLabel
                                        key={category.id}
                                        value={category.id}
                                        control={
                                            <Radio
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body1">
                                                {category.name}
                                            </Typography>
                                        }
                                        sx={{
                                            marginLeft: 0,
                                            marginRight: 0,
                                            padding: '0.75rem 1rem',
                                            borderRadius: '0.5rem',
                                            border: selectedCategoryIds.includes(category.id) ? '2px solid' : '1px solid',
                                            borderColor: selectedCategoryIds.includes(category.id) ? 'primary.main' : 'divider',
                                            backgroundColor: selectedCategoryIds.includes(category.id) ? 'action.selected' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                            flex: '0 0 auto',
                                        }}
                                    />
                                ))}
                            </RadioGroup>
                        ) : (
                            <FormGroup
                                row
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {categories.map((category) => (
                                    <FormControlLabel
                                        key={category.id}
                                        control={
                                            <Checkbox
                                                checked={selectedCategoryIds.includes(category.id)}
                                                onChange={handleCategoryChangeMultiple(category.id)}
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body1">
                                                {category.name}
                                            </Typography>
                                        }
                                        sx={{
                                            marginLeft: 0,
                                            marginRight: 0,
                                            padding: '0.75rem 1rem',
                                            borderRadius: '0.5rem',
                                            border: selectedCategoryIds.includes(category.id) ? '2px solid' : '1px solid',
                                            borderColor: selectedCategoryIds.includes(category.id) ? 'primary.main' : 'divider',
                                            backgroundColor: selectedCategoryIds.includes(category.id) ? 'action.selected' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                            flex: '0 0 auto',
                                        }}
                                    />
                                ))}
                            </FormGroup>
                        )}
                    </FormControl>
                </CardContent>
            </Card>

            {linkOnlySubCategories.length > 0 && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" fontWeight="bold" mb={1}>
                            תת-קטגוריה נקודתית
                        </Typography>
                        <Typography color="text.secondary" mb={2}>
                            מופיעה רק בקישור הזה, לא ברשימה הרגילה
                        </Typography>
                        <FormControl component="fieldset" fullWidth>
                            <RadioGroup
                                value={selectedSubCategoryId}
                                onChange={handleSubCategoryChange}
                            >
                                <FormControlLabel
                                    value=""
                                    control={<Radio />}
                                    label="בלי תת-קטגוריה נקודתית"
                                />
                                {linkOnlySubCategories.map((sub) => (
                                    <FormControlLabel
                                        key={sub.id}
                                        value={sub.id}
                                        control={<Radio />}
                                        label={`${sub.parentName} - ${sub.name}`}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </CardContent>
                </Card>
            )}

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        אוכלוסייה
                    </Typography>
                    <FormControl component="fieldset" fullWidth>
                        {selectionMode === 'single' ? (
                            <RadioGroup
                                value={selectedPopulationIds[0] || ''}
                                onChange={handlePopulationChangeSingle}
                                row
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {populations.map((population) => (
                                    <FormControlLabel
                                        key={population.id}
                                        value={population.id}
                                        control={
                                            <Radio
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body1">
                                                {population.name}
                                            </Typography>
                                        }
                                        sx={{
                                            marginLeft: 0,
                                            marginRight: 0,
                                            padding: '0.75rem 1rem',
                                            borderRadius: '0.5rem',
                                            border: selectedPopulationIds.includes(population.id) ? '2px solid' : '1px solid',
                                            borderColor: selectedPopulationIds.includes(population.id) ? 'primary.main' : 'divider',
                                            backgroundColor: selectedPopulationIds.includes(population.id) ? 'action.selected' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                            flex: '0 0 auto',
                                        }}
                                    />
                                ))}
                            </RadioGroup>
                        ) : (
                            <FormGroup
                                row
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {populations.map((population) => (
                                    <FormControlLabel
                                        key={population.id}
                                        control={
                                            <Checkbox
                                                checked={selectedPopulationIds.includes(population.id)}
                                                onChange={handlePopulationChangeMultiple(population.id)}
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body1">
                                                {population.name}
                                            </Typography>
                                        }
                                        sx={{
                                            marginLeft: 0,
                                            marginRight: 0,
                                            padding: '0.75rem 1rem',
                                            borderRadius: '0.5rem',
                                            border: selectedPopulationIds.includes(population.id) ? '2px solid' : '1px solid',
                                            borderColor: selectedPopulationIds.includes(population.id) ? 'primary.main' : 'divider',
                                            backgroundColor: selectedPopulationIds.includes(population.id) ? 'action.selected' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                            flex: '0 0 auto',
                                        }}
                                    />
                                ))}
                            </FormGroup>
                        )}
                    </FormControl>
                </CardContent>
            </Card>

            <Button
                variant="contained"
                onClick={handleGenerateLink}
                disabled={selectedCategoryIds.length === 0 && !selectedSubCategoryId}
                sx={{ alignSelf: 'flex-start', padding: '0.75rem 2rem' }}
            >
                יצירת קישור
            </Button>

            {generatedLink && (
                <TextField
                    label="קישור שנוצר"
                    value={generatedLink}
                    fullWidth
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleCopyLink} edge="end">
                                    <ContentCopyIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: '0.9rem',
                        },
                    }}
                />
            )}
        </Stack>
    );
};

export default CreateLinks;
